import React, { useState } from "react";
import { useCart } from "../../context/CartContext";
import "./CartPage.css";

// Site-wide discount: everything is 50% off, and item.price already
// holds the DISCOUNTED amount (set when the item was added to cart).
// So the original price is simply price * 2.
function getOriginalPrice(price) {
  return (price || 0) * 2;
}

function CartPage({ onNavigate }) {
  const { cartItems = [], subTotal = 0, updateQuantity, removeItem } = useCart();
  const [currentStep, setCurrentStep] = useState(1);
  const [showBankModal, setShowBankModal] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    streetAddress: "",
    city: "",
    postcode: "",
    country: "Pakistan",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Original (pre-discount) subtotal and total savings, based on the
  // 50% off rule — original = discounted * 2, savings = discounted amount.
  const originalSubTotal = subTotal * 2;
  const totalSavings = originalSubTotal - subTotal;

  const handleWhatsAppProceed = () => {
    // مدثر صاحب کا واٹس ایپ نمبر
    const phoneNo = "923006005249";

    const itemsList = cartItems
      .map(
        (item) =>
          `- ${item.name} (x${item.quantity}) - Rs. ${(
            item.price * item.quantity
          ).toLocaleString()}`
      )
      .join("\n");

    const message = `*New Order Placed - Muddassar Chemist*\n\n*Contact Information:*\n- Email: ${formData.email}\n- Phone: ${formData.phone}\n\n*Shipping Address:*\n- Name: ${formData.firstName} ${formData.lastName}\n- Address: ${formData.streetAddress}\n- City: ${formData.city}\n- Postcode: ${formData.postcode}\n- Country: ${formData.country}\n\n*Payment Method:* Bank Transfer (Receipt to be shared)\n\n*Order Items:*\n${itemsList}\n\n*Total to Pay (after 50% off):* Rs. ${subTotal.toLocaleString()}`;

    const whatsappUrl = `https://wa.me/${phoneNo}?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <main className="cart-page-wrapper">
      {/* Steps Header */}
      <div className="cart-steps-header">
        <span className={currentStep === 1 ? "step active" : "step"}>
          1. CART
        </span>
        <span className="step-separator">—</span>
        <span className={currentStep === 2 ? "step active" : "step"}>
          2. CHECKOUT
        </span>
        <span className="step-separator">—</span>
        <span className={showBankModal ? "step active" : "step"}>
          3. PAYMENT
        </span>
      </div>

      {/* Step 1: CART */}
      {currentStep === 1 && (
        <>
          {cartItems.length === 0 ? (
            <div className="empty-cart-card">
              <h2 className="empty-cart-title">
                Your shopping cart is currently empty.
              </h2>
              <button
                type="button"
                className="empty-cart-btn"
                onClick={() => onNavigate && onNavigate("/products")}
              >
                ← Explore Products
              </button>
            </div>
          ) : (
            <div className="cart-content-grid">
              <div className="cart-items-column">
                {cartItems.map((item) => (
                  <div key={item.id} className="cart-item-card">
                    <div className="item-img-container">
                      <img src={item.image} alt={item.name} />
                    </div>

                    <div className="item-info">
                      <h3 className="item-title">{item.name}</h3>
                      {item.subtitle && (
                        <p className="item-subtitle">{item.subtitle}</p>
                      )}
                      {item.details && (
                        <p className="item-specs">{item.details}</p>
                      )}

                      <div className="item-price-val">
                        <span className="original-price">
                          Rs. {getOriginalPrice(item.price).toLocaleString()}
                        </span>
                        <span className="discounted-price">
                          Rs. {(item.price || 0).toLocaleString()}
                        </span>
                        <span className="discount-tag">(50% OFF)</span>
                      </div>

                      <div className="quantity-controls">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, -1)}
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="delete-item-btn"
                      onClick={() => removeItem(item.id)}
                      title="Remove item"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      >
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="continue-btn"
                  onClick={() => onNavigate && onNavigate("/products")}
                >
                  ← CONTINUE SHOPPING
                </button>
              </div>

              <div className="order-summary-column">
                <div className="order-summary-card">
                  <h2 className="summary-title">Order Summary</h2>

                  <div className="summary-row">
                    <span>Sub Total</span>
                    <span className="summary-price-group">
                      <span className="original-price">
                        Rs. {originalSubTotal.toLocaleString()}
                      </span>
                      <span className="discounted-price">
                        Rs. {subTotal.toLocaleString()}
                      </span>
                    </span>
                  </div>

                  <div className="summary-row savings-row">
                    <span>You Save (50% OFF)</span>
                    <span>Rs. {totalSavings.toLocaleString()}</span>
                  </div>

                  <div className="summary-row">
                    <span>Shipping</span>
                    <span className="shipping-text">
                      calculated at checkout
                    </span>
                  </div>

                  <div className="summary-divider" />

                  <div className="summary-row total-row">
                    <span>Total</span>
                    <span>Rs. {subTotal.toLocaleString()}</span>
                  </div>

                  <button
                    type="button"
                    className="checkout-action-btn"
                    onClick={() => setCurrentStep(2)}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Step 2: CHECKOUT */}
      {currentStep === 2 && (
        <div className="cart-content-grid">
          <div className="checkout-form-column">
            <div className="form-card">
              <h2 className="form-section-title">Contact Information</h2>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="+92 300 6005249"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-card">
              <h2 className="form-section-title">Shipping Address</h2>
              <div className="form-row-2">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Street Address *</label>
                <input
                  type="text"
                  name="streetAddress"
                  placeholder="House number and street name"
                  value={formData.streetAddress}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>Town / City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Postcode *</label>
                  <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Country Region *</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                >
                  <option value="Pakistan">Pakistan</option>
                </select>
              </div>
            </div>
          </div>

          <div className="order-summary-column">
            <div className="order-summary-card">
              <h2 className="summary-title">Order Summary</h2>

              <div className="checkout-item-preview">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item-line">
                    <span>
                      {item.name} (x{item.quantity})
                    </span>
                    <span className="summary-price-group">
                      <span className="original-price">
                        Rs.{" "}
                        {getOriginalPrice(item.price * item.quantity)
                          .toLocaleString()}
                      </span>
                      <span className="discounted-price">
                        Rs. {(item.price * item.quantity).toLocaleString()}
                      </span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="summary-row">
                <span>Sub Total</span>
                <span className="summary-price-group">
                  <span className="original-price">
                    Rs. {originalSubTotal.toLocaleString()}
                  </span>
                  <span className="discounted-price">
                    Rs. {subTotal.toLocaleString()}
                  </span>
                </span>
              </div>

              <div className="summary-row savings-row">
                <span>You Save (50% OFF)</span>
                <span>Rs. {totalSavings.toLocaleString()}</span>
              </div>

              <div className="summary-row">
                <span>Shipping</span>
                <span className="shipping-text">calculated at checkout</span>
              </div>

              <div className="summary-divider" />

              <div className="summary-row total-row">
                <span>Total to Pay</span>
                <span>Rs. {subTotal.toLocaleString()}</span>
              </div>

              <button
                type="button"
                className="checkout-action-btn"
                onClick={() => setShowBankModal(true)}
              >
                View Bank Details & Pay
              </button>

              <button
                type="button"
                className="return-to-cart-link"
                onClick={() => setCurrentStep(1)}
              >
                ← Return to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Bank Modal */}
      {showBankModal && (
        <div className="modal-backdrop">
          <div className="bank-modal-card">
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowBankModal(false)}
            >
              ✕
            </button>

            <h2 className="bank-title">Payment Transfer Details</h2>

            <p className="bank-intro">
              Please transfer the total order amount using any of the payment options below:
            </p>

            <div className="bank-details-box">
              {/* EasyPaisa */}
              <div className="bank-info-section">
                <div className="bank-info-row">
                  <span className="info-label">Payment Method:</span>
                  <span className="info-value">EasyPaisa</span>
                </div>
                <div className="bank-info-row">
                  <span className="info-label">Account Title:</span>
                  <span className="info-value">Mudassar Iqbal</span>
                </div>
                <div className="bank-info-row">
                  <span className="info-label">Mobile No:</span>
                  <span className="info-value font-mono">03006005249</span>
                </div>
              </div>

              <hr style={{ margin: "12px 0", opacity: 0.2 }} />

              {/* Askari Bank */}
              <div className="bank-info-section">
                <div className="bank-info-row">
                  <span className="info-label">Bank Name:</span>
                  <span className="info-value">Askari Bank</span>
                </div>
                <div className="bank-info-row">
                  <span className="info-label">Account Title:</span>
                  <span className="info-value">Muddassir Iqbal</span>
                </div>
                <div className="bank-info-row">
                  <span className="info-label">IBAN / Account No:</span>
                  <span className="info-value font-mono">
                    PK71ASCM0001800900014934
                  </span>
                </div>
              </div>

              <hr style={{ margin: "12px 0", opacity: 0.2 }} />

              {/* JazzCash */}
              <div className="bank-info-section">
                <div className="bank-info-row">
                  <span className="info-label">Payment Method:</span>
                  <span className="info-value">JazzCash</span>
                </div>
                <div className="bank-info-row">
                  <span className="info-label">Account Title:</span>
                  <span className="info-value">Mudassar Iqbal</span>
                </div>
                <div className="bank-info-row">
                  <span className="info-label">Mobile No:</span>
                  <span className="info-value font-mono">03016005249</span>
                </div>
              </div>
            </div>

            <p className="bank-important-note">
              <strong>Important:</strong> After making the payment, please
              take a screenshot of your payment receipt. Then proceed below to send
              your receipt and order details to our WhatsApp.
            </p>

            <button
              type="button"
              className="whatsapp-submit-btn"
              onClick={handleWhatsAppProceed}
            >
              Proceed & Send Receipt to WhatsApp
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

export default CartPage;